/**
 * Test ID registry for QA automation.
 *
 * Single source of truth for `data-testid` attributes added to DOM elements.
 * The QA team consumes the same identifiers via `apps/lobby2.0/test-ids.json`,
 * which documents the patterns this file produces.
 *
 * Conventions:
 *  - All IDs are prefixed with `egt_lobby` (see {@link TEST_ID_PREFIX}).
 *  - Segments are snake_case to match the prefix.
 *  - Parameterized IDs are exposed as builder functions; the parameter value
 *    is normalized (lowercased) so callers can pass raw constants.
 *  - Builders are runtime-defensive: a missing/empty value resolves to
 *    `MISSING_PARAM_FALLBACK` instead of crashing the app, so a malformed
 *    API payload can never break rendering.
 *
 * When adding new IDs:
 *  1. Add the builder/constant under the appropriate feature namespace below.
 *  2. Mirror it in `apps/lobby2.0/test-ids.json` (pattern + examples).
 */

const TEST_ID_PREFIX = 'egt_lobby';

/**
 * Sentinel substituted for any missing/empty parameter passed to a builder.
 * Surfacing it as a recognizable string (rather than throwing or producing an
 * empty segment) makes the failure visible in DevTools without breaking tests.
 */
const MISSING_PARAM_FALLBACK = 'unknown';

/**
 * Lowercase a parameter for use inside an ID. Defensive against null,
 * undefined, empty strings, and whitespace-only strings — all of which
 * collapse to {@link MISSING_PARAM_FALLBACK} so the builder can never
 * throw on a malformed value. Internal whitespace runs are converted to
 * underscores so values that arrive in display form (e.g. the section
 * name 'Bell Link' instead of 'BELL_LINK') still produce the snake_case
 * IDs documented in test-ids.json.
 */
const normalize = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim();
  return (trimmed.length > 0 ? trimmed : MISSING_PARAM_FALLBACK).toLowerCase().replace(/\s+/g, '_');
};

export const testIds = {
  navigation: {
    /**
     * The clickable <div> that acts as a section tab button in the bottom
     * navigation bar (the element carrying `data-lobby-tab`). Use this when
     * you need to click the tab; use `tabLabel` when you need to assert the
     * displayed text.
     *
     * @param sectionName the section identifier (e.g. SECTION_NAMES.TOP_GAMES).
     *                    Pass the resolved/displayed name (post `getSectionName`).
     * @param state `'active'` when this tab is the currently-selected section,
     *              `'inactive'` otherwise. Driven by the same `isActive` prop
     *              that styles the tab, so the testid value flips live with
     *              the user's selection — QA can assert which tab is selected
     *              by the testid alone without inspecting any class.
     */
    tabButton: (sectionName: string, state: 'active' | 'inactive'): string =>
      `${TEST_ID_PREFIX}_nav_tab_${normalize(sectionName)}_btn_${normalize(state)}`,
    /**
     * The <span> element containing the translated display name of a
     * section tab in the bottom navigation bar.
     *
     * @param sectionName the section identifier (e.g. SECTION_NAMES.TOP_GAMES).
     *                    Pass the resolved/displayed name (post `getSectionName`)
     *                    so the ID matches what the user sees.
     */
    tabLabel: (sectionName: string): string => `${TEST_ID_PREFIX}_nav_tab_${normalize(sectionName)}`,
    /**
     * Desktop-only scroll arrow rendered on either side of the navigation bar
     * when the tab list overflows. Only visible while the user hovers the bar.
     */
    scrollArrow: (direction: 'left' | 'right'): string => `${TEST_ID_PREFIX}_nav_arrow_${normalize(direction)}`,
  },
  statusBar: {
    /** The settings (burger) button in the bottom status bar. */
    settingsButton: `${TEST_ID_PREFIX}_status_bar_settings_btn`,
    /** The search (magnifier) button in the bottom status bar. */
    searchButton: `${TEST_ID_PREFIX}_status_bar_search_btn`,
    /**
     * The container `<div>` of the player balance readout shown bottom-left
     * above the status bar. Wraps the amount, the currency suffix, and the
     * translated "Balance" caption. Always mounted while the status bar is
     * visible. Use the `balanceAmount` / `balanceCurrency` IDs to read the
     * two values separately without parsing concatenated text.
     */
    balance: `${TEST_ID_PREFIX}_status_bar_balance`,
    /**
     * The `<span>` inside the balance readout that displays the formatted
     * balance number only (e.g. "351 467.40"), without the currency.
     * Falls back to "0.00" while no balance has been received yet.
     */
    balanceAmount: `${TEST_ID_PREFIX}_status_bar_balance_amount`,
    /**
     * The `<span>` inside the balance readout that displays the currency
     * code only (e.g. "EUR"). Falls back to "USD" while no currency has
     * been received yet.
     */
    balanceCurrency: `${TEST_ID_PREFIX}_status_bar_balance_currency`,
  },
  sections: {
    /**
     * The <span> rendered inside a section header that displays the section's
     * translated title. Only one span is mounted at a time — the multi-view
     * variant shows the bare title, the single-view variant appends the games
     * count — both share this ID.
     *
     * @param sectionName the section identifier (e.g. SECTION_NAMES.TOP_GAMES).
     *                    Pass the resolved/displayed name (post `getSectionName`)
     *                    so the ID matches the navigation tab for the same section.
     */
    title: (sectionName: string): string => `${TEST_ID_PREFIX}_section_title_${normalize(sectionName)}`,
    /**
     * The clickable <span> styled as a link in the right slot of the section
     * header that displays "All (<count>)" plus a chevron. Clicking it switches
     * the section into single view; only mounted while the section is in
     * multi-view (single-view hides this element entirely).
     *
     * @param sectionName the section identifier (e.g. SECTION_NAMES.TOP_GAMES).
     *                    Pass the resolved/displayed name (post `getSectionName`)
     *                    so the ID matches the section title and the navigation
     *                    tab for the same section.
     */
    viewAllButton: (sectionName: string): string => `${TEST_ID_PREFIX}_section_view_all_${normalize(sectionName)}_btn`,
  },
  settings: {
    /**
     * The <h2> heading inside the settings modal that displays the title of
     * the currently-active tab (Language / Settings / Help). The displayed
     * text changes per tab; the ID is stable so QA can read the text to
     * assert which tab is open.
     */
    modalTitle: `${TEST_ID_PREFIX}_settings_modal_title`,
    /**
     * The "X" close button in the header of the settings modal. Static ID,
     * scoped to the settings modal only — other modals using the shared
     * `Modal` component do not receive this attribute.
     */
    modalCloseButton: `${TEST_ID_PREFIX}_settings_modal_close_btn`,
    /**
     * The informational paragraph on the Deposit tab, explaining that the balance
     * may lag a successful deposit. Only mounted while that tab is active, which
     * requires the `enableLobbyDeposit` flag and a non-demo session.
     */
    depositMessage: `${TEST_ID_PREFIX}_settings_deposit_message`,
    /**
     * A tab-switcher button rendered in the settings modal footer
     * (Language / Settings / Help / Deposit / Exit / Lobby).
     *
     * @param tab the tab key as defined in `features/settings/constants`
     *            (e.g. 'language', 'deposit'). Lowercased and `unknown`-fallback
     *            handled by the registry.
     */
    tabButton: (tab: string): string => `${TEST_ID_PREFIX}_settings_tab_${normalize(tab)}_btn`,
    /**
     * A single clickable language row in the language picker of the settings
     * modal. This is the element the user interacts with to switch language;
     * the inner radio circle is visual-only.
     *
     * @param langCode lowercase ISO language code as exposed by the app
     *                 (e.g. 'en', 'de', 'bg'). Whatever value is supplied is
     *                 normalized — null/undefined/empty fall back to `unknown`.
     */
    languageOption: (langCode: string): string => `${TEST_ID_PREFIX}_settings_language_${normalize(langCode)}`,
    /**
     * A toggle switch (the `<label>` carrying the on/off state) inside the
     * Settings tab of the settings modal. The `state` segment reflects the
     * toggle's *current* value — when the user clicks the toggle, the testid
     * value flips, so QA can assert state by the testid alone without
     * inspecting any class or aria attribute.
     *
     * @param name identifier of the toggle. Today the known values are
     *             `sound` and `continue_playing`; left as `string` so future
     *             toggles do not require a type change. Normalized to
     *             lowercase; missing values fall back to `unknown`.
     * @param state `'on'` when the toggle is currently enabled, `'off'`
     *              otherwise. Driven by the same boolean that feeds the
     *              underlying `<ToggleSwitch>`'s `checked` prop.
     */
    toggle: (name: string, state: 'on' | 'off'): string =>
      `${TEST_ID_PREFIX}_settings_${normalize(name)}_toggle_${normalize(state)}`,
    /**
     * The title `<div>` of a toggle row inside the Settings tab of the
     * settings modal (e.g. the text "Continue playing" / "Sounds"). This is
     * the translated label next to the toggle switch, NOT the switch itself
     * — use `toggle` for the switch.
     *
     * @param name identifier of the toggle row. Same values as `toggle`:
     *             today `sound` and `continue_playing`.
     */
    toggleLabel: (name: string): string => `${TEST_ID_PREFIX}_settings_${normalize(name)}_toggle_label`,
    /**
     * The subtitle/description `<div>` of a toggle row inside the Settings
     * tab of the settings modal — the smaller line of text rendered directly
     * under the row title.
     *
     * @param name identifier of the toggle row. Same values as `toggle`:
     *             today `sound` and `continue_playing`.
     */
    toggleDescription: (name: string): string => `${TEST_ID_PREFIX}_settings_${normalize(name)}_toggle_description`,
  },
  games: {
    /**
     * The clickable container `<div>` of the back-to-game toast ("Continue
     * playing" + game name) shown at the bottom of the lobby after the
     * player returns from a game. Only one toast is mounted at a time
     * (toast id `back-to-game`); clicking anywhere on it relaunches the
     * game. Carries `data-continue-playing="on|off"` mirroring the
     * Continue Playing setting.
     */
    backToGameToast: `${TEST_ID_PREFIX}_back_to_game_toast`,
    /**
     * The `<span>` inside the back-to-game toast that displays the
     * translated "Continue Playing" label.
     */
    backToGameContinueLabel: `${TEST_ID_PREFIX}_back_to_game_continue_label`,
    /**
     * The `<span>` inside the back-to-game toast that displays the name of
     * the game the player can resume (e.g. "Flaming Hot Extreme Bell Link").
     */
    backToGameName: `${TEST_ID_PREFIX}_back_to_game_name`,
    /**
     * The "TOP" corner ribbon `<div>` rendered on a game card when the game
     * is flagged as a top game. Mutually exclusive with the NEW ribbon and
     * with the Hot&Cold RTP banner (which takes priority on Hot&Cold cards).
     * Static ID shared by every TOP ribbon in the DOM — scope queries via
     * the hosting game card / section when a specific game matters.
     */
    topGameBadge: `${TEST_ID_PREFIX}_game_top_badge`,
    /**
     * The "NEW" corner ribbon `<div>` rendered on a game card when the game
     * is flagged as new (or when the card is rendered inside the New
     * Releases section). Same multiplicity caveat as `topGameBadge`.
     */
    newGameBadge: `${TEST_ID_PREFIX}_game_new_badge`,
  },
  jackpots: {
    /**
     * The outer container `<div>` of the linked-jackpot display widget
     * rendered at the start of a jackpot section (the panel with the jackpot
     * logo and the four GRAND/MAJOR/MINOR/MINI level values). Mounted once
     * per jackpot section, so the section segment makes it unique per
     * section even when two sections share the same jackpot family (e.g.
     * High Cash vs VIP High Cash).
     *
     * @param sectionName the jackpot section's name as supplied to the
     *                    panel. Arrives either as the SECTION_NAMES constant
     *                    ('BELL_LINK') or in display form ('Bell Link') —
     *                    both normalize to the same snake_case segment
     *                    (e.g. 'bell_link').
     */
    panel: (sectionName: string): string => `${TEST_ID_PREFIX}_jackpot_panel_${normalize(sectionName)}`,
    /**
     * The container `<div>` of the "LINKED JACKPOTS … ALL IN ONE" strip
     * inside a jackpot section's display widget — the row of mini
     * jackpot-logo cards. Only mounted when the section participates in a
     * linked group of two or more jackpots.
     *
     * @param sectionName the jackpot section the strip is rendered in. Same
     *                    resolution as `panel`, so the strip and its parent
     *                    widget share the section key.
     */
    linkedLogos: (sectionName: string): string => `${TEST_ID_PREFIX}_jackpot_linked_logos_${normalize(sectionName)}`,
    /*
     * Statistics drawer ids. Behaviour details (when elements mount, hide, renumber) live in
     * test-ids.json — the QA-facing catalog — and are not repeated here.
     */
    /** Full-screen backdrop of the statistics drawer; clicking it (and only it) closes. */
    statisticsOverlay: (sectionName: string): string =>
      `${TEST_ID_PREFIX}_jackpot_statistics_overlay_${normalize(sectionName)}`,
    /** The drawer panel itself. @param jackpotName instance name, e.g. 'bell_link'. */
    statisticsDrawer: (jackpotName: string): string =>
      `${TEST_ID_PREFIX}_jackpot_statistics_drawer_${normalize(jackpotName)}`,
    /** Header panel (thumbnail, caption, level wordmark, amount). */
    statisticsHeader: `${TEST_ID_PREFIX}_jackpot_statistics_header`,
    /** One win row. @param rank 1-based position in the CURRENTLY FILTERED list. */
    statisticsRow: (rank: number): string => `${TEST_ID_PREFIX}_jackpot_statistics_row_${rank}`,
    /** A row's game-thumbnail launch button. @param gameKey e.g. 'BLSlot'. */
    statisticsGameButton: (gameKey: string): string =>
      `${TEST_ID_PREFIX}_jackpot_statistics_game_btn_${normalize(gameKey)}`,
    /**
     * A level filter chip in the footer. Levels with 0 wins get no chip at all.
     *
     * @param level display level name, lowercased ('grand' … / 'super', 'middle' on Premium Link).
     * @param state `'on'` while the chip is an active filter, `'off'` otherwise.
     */
    statisticsLevelFilter: (level: string, state: 'on' | 'off'): string =>
      `${TEST_ID_PREFIX}_jackpot_statistics_level_${normalize(level)}_${normalize(state)}`,
    /**
     * One of the two ordering buttons in the footer; exactly one is `on` at any time.
     *
     * @param mode `'last_100'` or `'top_10'`.
     * @param state `'on'` while this ordering is selected, `'off'` otherwise.
     */
    statisticsSortFilter: (mode: string, state: 'on' | 'off'): string =>
      `${TEST_ID_PREFIX}_jackpot_statistics_sort_${normalize(mode)}_${normalize(state)}`,
    /** The "X" close button in the drawer footer. */
    statisticsCloseButton: `${TEST_ID_PREFIX}_jackpot_statistics_close_btn`,
    /** The fireworks placeholder shown in place of the rows when the current filter has no wins. */
    statisticsEmptyState: `${TEST_ID_PREFIX}_jackpot_statistics_empty`,
  },
  campaigns: {
    /**
     * A tab-switcher `<span>` in a campaign single view (the Tournaments
     * page tabs Games / Rewards / Ranking / Rules, or the PrizePop tabs
     * Games / Prizes / Winners / Rules). The `state` segment reflects which
     * tab is currently selected and flips live with the user's selection —
     * QA can assert the active tab by the testid alone without inspecting
     * any class.
     *
     * @param campaignType `'tournaments'` or `'prize_pop'` (CampaignTypes).
     * @param tab the tab key from the campaign tabs config: for tournaments
     *            `games` / `rewards` / `ranking` / `rules`; for prize_pop
     *            `games` / `prizes` / `players` / `rules`.
     * @param state `'active'` when this tab is the currently-selected one,
     *              `'inactive'` otherwise.
     */
    tabButton: (campaignType: string, tab: string, state: 'active' | 'inactive'): string =>
      `${TEST_ID_PREFIX}_${normalize(campaignType)}_tab_${normalize(tab)}_btn_${normalize(state)}`,
    /**
     * The rules content box `<div>` inside the Rules tab of a campaign
     * single view — the rounded dark container directly under the tab strip
     * that wraps the entire rules content: the section headings, the body
     * text, and the "i" info button in its top-right corner. Emitted by both
     * the tournaments and the PrizePop Rules tabs. Only mounted while the
     * Rules tab is selected AND the campaign data has loaded — the loading
     * placeholder does not carry this ID.
     *
     * @param campaignType `'tournaments'` or `'prize_pop'` (CampaignTypes).
     */
    rulesContent: (campaignType: string): string => `${TEST_ID_PREFIX}_${normalize(campaignType)}_rules_content`,
    /**
     * The "Join In" button `<div>` of a campaign. Rendered on the campaign
     * card in single view (`variant: 'card'`) and on the multi-view section
     * panel (`variant: 'panel'`); only mounted when the campaign is not
     * auto-join. Both lobby views stay mounted simultaneously (hidden, not
     * unmounted), so the card and panel buttons of the same campaign type
     * can coexist in the DOM — the `variant` segment disambiguates them.
     * Joined state is NOT part of the ID; assert it via the button's visible
     * text ("Join In" vs "Joined").
     *
     * @param campaignType `'tournaments'` or `'prize_pop'` (CampaignTypes).
     * @param variant `'card'` (single-view campaign card) or `'panel'`
     *                (multi-view section panel).
     */
    joinInButton: (campaignType: string, variant: string): string =>
      `${TEST_ID_PREFIX}_${normalize(campaignType)}_join_in_btn_${normalize(variant)}`,
    /**
     * The container `<div>` of the countdown/date item in a campaign card's
     * bottom info bar — wraps both the label ("Ends On" / "Ends In" /
     * "Starts On" / "Starts In" plus the clock icon) and the value (either
     * the formatted date "01 JUN" when more than 3 days remain, or the live
     * HH:MM:SS countdown). A card renders exactly one timer item (starts OR
     * ends, never both), so the ID carries no direction segment. The
     * countdown ticks via direct DOM updates, so re-read text content per
     * assertion. One element per campaign card — when a carousel shows
     * several campaigns of the same type, scope the query to the desired
     * card.
     *
     * @param campaignType `'tournaments'` or `'prize_pop'` (CampaignTypes).
     */
    timer: (campaignType: string): string => `${TEST_ID_PREFIX}_${normalize(campaignType)}_timer`,
  },
  search: {
    /**
     * The back arrow button in the header of the search overlay. Closes the
     * overlay and returns the user to the previous view. Static ID — only
     * rendered while the search overlay is active.
     */
    backButton: `${TEST_ID_PREFIX}_search_back_btn`,
    /**
     * The randomizer (shuffle) button in the header of the search overlay.
     * Opens the random game modal. The `state` segment reflects whether the
     * random modal is currently open and flips with the user's interaction —
     * QA can assert open/closed by the testid alone without inspecting any
     * class or aria attribute.
     *
     * @param state `'open'` when the random modal is currently mounted,
     *              `'closed'` otherwise. Driven by the same boolean that
     *              decides whether to render `<RandomGameModal>`.
     */
    randomizerButton: (state: 'open' | 'closed'): string =>
      `${TEST_ID_PREFIX}_search_randomizer_btn_${normalize(state)}`,
    /**
     * The filter button in the header of the search overlay. Toggles the
     * filter panel below the header. The `state` segment reflects whether the
     * filter panel is currently open and flips with the user's interaction —
     * QA can assert open/closed by the testid alone without inspecting any
     * class or aria attribute.
     *
     * @param state `'open'` when the filter panel is currently mounted,
     *              `'closed'` otherwise. Driven by the same boolean that
     *              decides whether to render `<FilterPanel>`.
     */
    filterButton: (state: 'open' | 'closed'): string => `${TEST_ID_PREFIX}_search_filter_btn_${normalize(state)}`,
  },
} as const;
